window.bootCartRecommendations = {
  swiper: null,
  isLoading: false,
  lastCartHash: '',
  selectors: {
    inner: '#CartDrawer-Recommendations .cart-drawer-recommendations__inner',
  },

  getContainer() {
    const container = document.querySelector(this.selectors.inner);
    if (!container) console.error('Container not found!');
    return container;
  },

  async init(force = false) {
    if (this.getContainer()) await this.load(force);
  },

  async load(force = false) {
    if (this.isLoading || !this.getContainer()) return;

    this.isLoading = true;
    try {
      const cart = await this.getCart();
      if (!cart || !cart.items || !cart.items.length) {
        this.clear();
        this.destroySwiper();
        this.lastCartHash = '';
        return;
      }

      const cartHash = this.getCartHash(cart);
      const storedRecommendations = localStorage.getItem('cartRecommendations_v2');

      if (storedRecommendations) {
        const cachedProducts = JSON.parse(storedRecommendations);
        if (cachedProducts.length && (force || cartHash === this.lastCartHash)) {
          this.render(cachedProducts.slice(0, 6));
          this.initSwiper();
          if (!force) {
            this.isLoading = false;
            return;
          }
        }
      }

      const products = await this.getRecommendationsFromCart(cart);
      if (!products.length) {
        this.clear();
        this.destroySwiper();
        this.lastCartHash = cartHash;
        return;
      }

      localStorage.setItem('cartRecommendations_v2', JSON.stringify(products));
      this.render(products);
      this.initSwiper();
      this.lastCartHash = cartHash;
    } catch (error) {
      console.error('Cart recommendations error:', error);
      this.clear();
      this.destroySwiper();
    } finally {
      this.isLoading = false;
    }
  },

  render(products) {
    const container = this.getContainer();
    if (!container) return;

    container.innerHTML = `
      <div class="cart-upsell-block">
        <div class="cart-upsell-block__header">
          <h6 class="cart-upsell-block__title">Recommended for you</h6>
          <div class="cart-upsell-nav">
            <button type="button" class="cart-upsell-prev" aria-label="Previous recommendation">
              <span aria-hidden="true">\u2039</span>
            </button>
            <button type="button" class="cart-upsell-next" aria-label="Next recommendation">
              <span aria-hidden="true">\u203A</span>
            </button>
          </div>
        </div>

        <div class="cart-upsell-swiper swiper">
          <div class="swiper-wrapper">
            ${products
              .map((product) => {
                const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
                if (!variant) return '';

                const image = this.getProductImage(product);
                const price = this.formatMoney(product.price);
                const compareAt =
                  product.compare_at_price && product.compare_at_price > product.price
                    ? this.formatMoney(product.compare_at_price)
                    : '';

                return `
                <div class="swiper-slide">
                  <div class="cart-upsell-card">
                    <a href="${product.url}" class="cart-upsell-card__image-link">
                      <img
                        class="cart-upsell-card__image"
                        src="${image}"
                        alt="${this.escapeHtml(product.title)}"
                        loading="lazy"
                        width="96"
                        height="96"
                      >
                    </a>
                    <div class="cart-upsell-card__content">
                      <a href="${product.url}" class="cart-upsell-card__title cart-item__name h4 break">
                        ${this.escapeHtml(product.title)}
                      </a>
                      <div class="cart-upsell-card__bottom">
                        <div class="cart-upsell-card__price-wrap">
                          <span class="cart-upsell-card__price">${price}</span>
                          ${compareAt ? `<s class="cart-upsell-card__compare">${compareAt}</s>` : ''}
                        </div>
                        <button
                          type="button"
                          class="cart-upsell-card__button"
                          data-upsell-add
                          data-variant-id="${variant.id}"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              `;
              })
              .join('')}
          </div>
        </div>
      </div>
    `;

    this.bindAddButtons();
  },

  initSwiper() {
    const container = this.getContainer();
    if (!container) return;

    const slider = container.querySelector('.cart-upsell-swiper');
    if (!slider || typeof Swiper === 'undefined') return;

    this.destroySwiper();
    this.swiper = new Swiper(slider, {
      slidesPerView: 1,
      spaceBetween: 10,
      speed: 400,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      navigation: {
        nextEl: container.querySelector('.cart-upsell-next'),
        prevEl: container.querySelector('.cart-upsell-prev'),
      },
      breakpoints: {
        480: { slidesPerView: 1, spaceBetween: 10 },
        768: { slidesPerView: 1, spaceBetween: 12 },
      },
    });
  },

  destroySwiper() {
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  },

  bindAddButtons() {
    const container = this.getContainer();
    if (!container) return;

    container.querySelectorAll('[data-upsell-add]').forEach((button) => {
      button.addEventListener('click', async () => {
        const variantId = button.getAttribute('data-variant-id');
        if (!variantId) return;

        button.disabled = true;
        button.textContent = 'Adding...';

        try {
          const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          });

          if (!addResponse.ok) throw new Error('Failed to add upsell product');

          const cartData = await (
            await fetch('/cart.js', { headers: { Accept: 'application/json' } })
          ).json();

          if (window.updateKikCartDrawerTotal) window.updateKikCartDrawerTotal(cartData);
          if (window.updateCartCount) await window.updateCartCount(cartData.item_count);
          if (window.updateCartUI) await window.updateCartUI();
          else if (window.empty_updateCartUI) await window.empty_updateCartUI();

          setTimeout(() => {
            this.refresh(true);
          }, 150);
        } catch (error) {
          console.error('Upsell add failed:', error);
        } finally {
          button.disabled = false;
          button.textContent = 'Add';
        }
      });
    });
  },

  clear() {
    const container = this.getContainer();
    if (container) container.innerHTML = '';
    localStorage.removeItem('cartRecommendations_v2');
    localStorage.removeItem('cartRecommendations');
  },

  async refresh(force = true) {
    await this.load(force);
  },

  async getCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  },

  getCartHash(cart) {
    return (cart.items || [])
      .map((item) => `${item.product_id}:${item.variant_id}:${item.quantity}`)
      .join('|');
  },

  async getRecommendationsFromCart(cart) {
    const allProducts = [];
    const seenProductIds = new Set();

    for (const item of [...cart.items].reverse()) {
      let products = await this.fetchRecommendations(item.product_id, 'complementary');
      if (!products.length) {
        products = await this.fetchRecommendations(item.product_id, 'related');
      }

      this.filterProducts(products, cart).forEach((product) => {
        const id = Number(product.id);
        if (!seenProductIds.has(id)) {
          seenProductIds.add(id);
          allProducts.push(product);
        }
      });
    }

    return allProducts;
  },

  async fetchRecommendations(productId, intent = 'related') {
    const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=8&intent=${intent}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    return data.products || [];
  },

  filterProducts(products, cart) {
    const cartProductIds = cart.items.map((item) => Number(item.product_id));
    const cartVariantIds = cart.items.map((item) => Number(item.variant_id));

    return products.filter((product) => {
      const firstAvailableVariant = product.variants?.find((variant) => variant.available);
      return (
        product.available &&
        !cartProductIds.includes(Number(product.id)) &&
        firstAvailableVariant &&
        !cartVariantIds.includes(Number(firstAvailableVariant.id))
      );
    });
  },

  formatMoney(cents) {
    if (!Number.isFinite(cents)) return '';

    const shopify = window.Shopify || {};
    const moneyFormat = shopify.money_format || window.themeMoneyFormat;

    if (typeof shopify.formatMoney === 'function' && moneyFormat) {
      return shopify.formatMoney(cents, moneyFormat);
    }

    const currency = shopify.currency?.active || window.cartCurrency || 'USD';
    const amount = cents / 100;
    const noFractionCurrencies = ['BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];
    const hasFraction = cents % 100 !== 0 && !noFractionCurrencies.includes(currency);

    try {
      return amount.toLocaleString(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: hasFraction ? 2 : 0,
      });
    } catch (error) {
      return hasFraction ? `${currency} ${amount.toFixed(2)}` : `${currency} ${Math.round(amount)}`;
    }
  },

  getProductImage(product) {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.url) return product.featured_image.url;
    if (product.images?.length) return product.images[0];
    return '';
  },

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  },
};

window.addEventListener('load', () => bootCartRecommendations.init());

window.bootCartRecommendations.refresh = async function (force = true) {
  const stored = localStorage.getItem('cartRecommendations_v2');
  if (stored) {
    const products = JSON.parse(stored);
    if (products.length) this.render(products);
  }
  await this.load(force);
};
